import { lazy } from "react";
import { IRC, IRC_Exportable, IRC_Exportable_PPTX_Chart, IRC_Exportable_PPTX_Table } from "../../../types";
import { legend } from "@/core/utils/legend-state/core";
import { Observable } from "@legendapp/state";
import { createContextProvider } from "@/core/utils/context-api";
import { state, IState } from "./instance/state";
import { report_builder } from "@/features/report-builder/state";
import { hslToHex } from "@/core/utils/tailwind-toolkit/utils";
import { sanitizeForCSV } from "../../../helpers";
import { capitalize } from "lodash";
import pptxgen from "pptxgenjs";

function mapMeasureNameToLabel(name: string) {
  if (name === "PROMOTER_RATIO_PER_COMPANY" || name === "PROMOTER_COUNT_PER_COMPANY") {
    return "Promoters";
  }
  if (name === "DETRACTOR_RATIO_PER_COMPANY" || name === "DETRACTOR_COUNT_PER_COMPANY") {
    return "Detractors";
  }
  if (name === "PASSIVE_RATIO_PER_COMPANY" || name === "PASSIVE_COUNT_PER_COMPANY") {
    return "Passive";
  }
}

function mapMeasureNameToColor(name: string, theme: string) {
  if (name === "PROMOTER_RATIO_PER_COMPANY" || name === "PROMOTER_COUNT_PER_COMPANY") {
    return theme === "light" ? "#6DDBB0" : "#2F7B62";
  }
  if (name === "DETRACTOR_RATIO_PER_COMPANY" || name === "DETRACTOR_COUNT_PER_COMPANY") {
    return theme === "light" ? "#FF5555" : "#6A0000";
  }
  if (name === "PASSIVE_RATIO_PER_COMPANY" || name === "PASSIVE_COUNT_PER_COMPANY") {
    return theme === "light" ? "#FFD199" : "#1C1C1F";
  }
}

// Helper function to ensure consistent key ordering
function getOrderedDataKeys(chartData: any[], axisKey: string): string[] {
  const allKeys = Object.keys(chartData[0]).filter((key) => key !== axisKey);

  // Define the desired order keywords (case-insensitive)
  const orderKeywords = ["PROMOTER", "PASSIVE", "DETRACTOR"];

  // Categorize keys by keywords
  const categorizedKeys: string[][] = orderKeywords.map(() => []);
  const remainingKeys: string[] = [];

  allKeys.forEach((key) => {
    const upperKey = key.toUpperCase();
    const categoryIndex = orderKeywords.findIndex((keyword) => upperKey.includes(keyword));

    if (categoryIndex !== -1) {
      categorizedKeys[categoryIndex].push(key);
    } else {
      remainingKeys.push(key);
    }
  });

  // Flatten categorized keys in order and add remaining keys
  return [...categorizedKeys.flat(), ...remainingKeys];
}

const RC_ID = "employee_positivity";

export const rc: IRC<IState.ILocalState$, IState.IRemoteState$, { instance_id: string }> = {
  rc_id: RC_ID,
  instance: {
    component: lazy(() =>
      import("./instance/ui/index").then((module) => ({
        default: module.RC_Component,
      })),
    ),
  },
  registry: {
    state$: legend.state.observable<{
      local_instances: Record<string, Observable<IState.ILocalState$>>;
      remote_instances: Record<string, Observable<IState.IRemoteState$>>;
    }>({
      local_instances: {},
      remote_instances: {},
    }),
    context: createContextProvider<{
      instance_id: string;
    }>(),
    actions: {
      createInstance: (instance_id, options) => {
        const { local, remote } = state.createState(instance_id, { local: options.local });

        legend.state.batch(() => {
          rc.registry.state$.local_instances[instance_id].set(local);
          rc.registry.state$.remote_instances[instance_id].set(remote);

          report_builder.local.active_rc_registries.assign({
            [RC_ID]: RC_ID,
          });
        });
      },
      deleteInstance: (id) => {
        legend.state.batch(() => {
          rc.registry.state$.remote_instances[id].delete();
          rc.registry.state$.local_instances[id].delete();
          if (rc.registry.actions.isEmpty()) {
            report_builder.local.active_rc_registries[RC_ID].delete();
          }
        });
      },
      cleanUp: () => {
        legend.state.batch(() => {
          rc.registry.state$.remote_instances.set({});
          rc.registry.state$.local_instances.set({});
          report_builder.local.active_rc_registries[RC_ID].delete();
        });
        // TODO: clean up local storage data and remote data if cache is effected
      },
      getInstance: (id) => {
        const remote = rc.registry.state$.remote_instances[id];
        const local = rc.registry.state$.local_instances[id];
        if (!remote) {
          throw new Error(`Remote instance with id ${id} not found`);
        }
        if (!local) {
          throw new Error(`Local instance with id ${id} not found`);
        }
        return { remote, local };
      },
      isEmpty: () => {
        return (
          Object.keys(rc.registry.state$.remote_instances.get()).length === 0 &&
          Object.keys(rc.registry.state$.local_instances.get()).length === 0
        );
      },
      exportInstance: async (id) => {
        const instance = rc.registry.actions.getInstance(id);

        const exportable: IRC_Exportable = {
          folder_path: "how-positive-are-the-employee-ratings-for-a-company",
          master: {
            pptx: {
              powerpoint_name: "how-positive-are-the-employee-ratings-for-a-company",
              merge_views_to_master_pptx: true,
            },
          },
          formats: [
            {
              pptx: {
                powerpoint_name: "how-positive-are-the-employee-ratings-for-a-company",
                slide_data: [
                  {
                    title: "Analysis: How positive are the employee ratings for a Company?",
                    chart: await helpers.views.analysis.visualisation.pptx.eps_bar(instance),
                  },
                  {
                    title: "Analysis: How positive are the employee ratings for a Company?",
                    table: await helpers.views.analysis.visualisation.pptx.eps_table(instance),
                  },
                  {
                    title: "Analysis: How positive are the employee ratings for a Company?",
                    chart: await helpers.views.analysis.visualisation.pptx.bar(instance),
                  },
                  {
                    title: "Analysis: How positive are the employee ratings for a Company?",
                    table: await helpers.views.analysis.visualisation.pptx.table(instance),
                  },
                ],
              },
              csv: {
                data: [
                  {
                    name: "Employee-Rating-Composition",
                    content: await helpers.views.analysis.visualisation.csv(instance),
                  },
                  {
                    name: "Employee-Positivity-Score",
                    content: await helpers.views.analysis.visualisation.eps_csv(instance),
                  },
                ],
              },
            },
          ],
        };

        return exportable;
      },
    },
  },
};
// #endregion

const helpers = {
  views: {
    analysis: {
      visualisation: {
        pptx: {
          eps_bar: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
            // Get measures_data which contains eps_data
            const measures_data = await legend.utils.awaitObservable(
              instance.remote.measures.data,
              instance.remote.measures.pending,
              instance.remote.measures.error,
            );

            const peerset = report_builder.remote.peerset.data.get();

            // Get EPS data and transform for bar chart
            const eps_data = measures_data.eps_data || [];

            if (!eps_data.length) {
              throw new Error("No EPS data available for chart generation");
            }

            // Transform EPS data for bar chart visualization
            const chartData = eps_data
              .map((measure, idx) => {
                const company = peerset[measure.company_id];
                const score =
                  typeof measure.measure_value === "string"
                    ? parseFloat(measure.measure_value.replace(/[+\-%]/g, ""))
                    : measure.measure_value;

                return {
                  company_name: `#${idx + 1} ${company?.company_name}` || "Unknown Company",
                  eps_score: score || 0,
                  display_value: typeof measure.measure_value === "string" ? measure.measure_value : `${score || 0}%`,
                };
              })
              .sort((a, b) => b.eps_score - a.eps_score); // Sort by EPS score descending

            const categories = chartData.map((item) => item.company_name);
            const values = chartData.map((item) => item.eps_score);

            // Create PPTX bar chart
            const pptx_slide: IRC_Exportable_PPTX_Chart = {
              type: "bar",
              data: [
                {
                  name: "Net Rating",
                  labels: categories.reverse(),
                  values: values.reverse(),
                  dashType: "solid",
                },
              ],
              options: {
                title: "Net Rating by Company",
                showTitle: true,
                titleFontSize: 14,
                x: 0.5,
                y: 0.9,
                w: "90%",
                h: "80%",
                chartColors: ["black"], // Single color for EPS bars
                showLegend: false, // No legend needed for single series

                // Configure for vertical bars
                barDir: "bar",

                // Add data labels on bars
                showValue: true,
                dataLabelPosition: "outEnd", // Position labels at end of bars
                dataLabelFontSize: 10,
                dataLabelColor: "363636",
                dataLabelFontFace: "Arial",
                dataLabelFormatCode: `0.0"%"`, // Format as percentage

                chartArea: { border: { type: "solid", color: "888888", pt: 1 } },
                lineSize: 2,
                showCatAxisTitle: true,
                catAxisTitle: "Company",
                showValAxisTitle: true,
                valAxisTitle: "Net Rating (%)",
                valAxisLabelFontSize: 10,
                valAxisLabelFontFace: "Arial",
                valAxisLabelColor: "363636",
                catAxisLabelFontSize: 9,
                catAxisLabelColor: "363636",
                catAxisLabelFontFace: "Arial",
              },
            };

            return pptx_slide;
          },
          bar: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
            // measures_data is a remote observable, so we need to await it whilst it is fetched from the server
            const measures_data = await legend.utils.awaitObservable(
              instance.remote.measures.data,
              instance.remote.measures.pending,
              instance.remote.measures.error,
            );

            const chart_config = instance.local.views.analysis.chart_config.get();
            const peerset = report_builder.remote.peerset.data.get();
            const current_measure_id = instance.local.views.analysis.selectors.measure.selectedMeasureId.get();
            const current_measure = instance.local.config.measure.measures
              .get()
              .find((measure) => measure.id === current_measure_id);

            // Get all unique keys except the category key (xAxisDataKey)
            const axisKey = chart_config.polar_axis.data_key;
            if (!axisKey) {
              throw new Error("X-axis data key not found");
            }
            const categories = measures_data.chart_data.map((item, idx) => {
              const id = item[axisKey] as string;
              return `#${idx + 1} ${peerset[id].company_name}`;
            });

            // Transform data for each series with hex color conversion
            const seriesData = getOrderedDataKeys(measures_data.chart_data, axisKey).map((key, idx) => {
              const isBenchmark = key.startsWith("benchmark_");

              const output = {
                name: mapMeasureNameToLabel(key),
                labels: categories,
                values: measures_data.chart_data.map((item) => Number(item[key]) || 0),
                color: mapMeasureNameToColor(key, "light") ?? "",
                dashType: isBenchmark ? "dash" : "solid",
              };
              return output;
            });

            // create PPTX
            const pptx_slide: IRC_Exportable_PPTX_Chart = {
              type: "bar",
              data: seriesData.map((series) => ({
                name: series.name,
                labels: series.labels.reverse(),
                values: series.values.reverse(),
                dashType: series.dashType,
              })),
              options: {
                title: `${current_measure?.label}`,
                showTitle: true,
                titleFontSize: 12,
                x: 0.5,
                y: 0.9,
                w: "90%",
                h: "80%",
                chartColors: seriesData.map((series) => series.color),
                showLegend: true,
                legendPos: "b",

                // Configure for stacked vertical bars
                barDir: "bar",
                barGrouping: "stacked",

                // Add data labels in the center of bars
                showValue: true, // Enable showing values
                dataLabelPosition: "ctr", // Position labels in center
                dataLabelFontSize: 10, // Optional: customize font size
                dataLabelColor: "FFFFFF", // Optional: white text for better contrast
                dataLabelFontFace: "Arial", // Optional: customize font
                dataLabelFormatCode: `0.0"${current_measure?.unit ?? ""}"`,

                chartArea: { border: { type: "solid", color: "888888", pt: 1 } },
                lineSize: 2,
                showCatAxisTitle: false,
                valAxisTitle: current_measure?.label || "",
                showValAxisTitle: true,
                valAxisLabelFontSize: 10,
                valAxisLabelFontFace: "Arial",
                valAxisLabelColor: "363636",
                catAxisLabelFontSize: 8,
              },
            };

            return pptx_slide;
          },
          table: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
            const table_data = (
              await legend.utils.awaitObservable(
                instance.remote.measures.data,
                instance.remote.measures.pending,
                instance.remote.measures.error,
              )
            ).table_data;

            const current_measure_id = instance.local.views.analysis.selectors.measure.selectedMeasureId.get();
            const current_measure = instance.local.config.measure.measures
              .get()
              .find((measure) => measure.id === current_measure_id);

            const measure_names =
              instance.local.config.company.measure_filter_to_measure_name_map.get()[current_measure_id].default;

            const selected_peer_set_avg_type = instance.local.views.analysis.selectors.peer_set_avg_type.applied.get();
            const peer_set_type =
              instance.local.config.peer_set_avg.get()[
                selected_peer_set_avg_type as "PEER_SET_AVG_MEAN" | "PEER_SET_AVG_MEDIAN"
              ];

            const table: pptxgen.TableRow[] = [];
            const row: pptxgen.TableRow = [
              {
                text: "Rank",
              },
              {
                text: "Company",
              },
            ];

            measure_names.forEach((measure_name) => {
              const label = mapMeasureNameToLabel(measure_name);
              row.push({
                text: label,
              });
            });

            table.push(row);

            table_data.forEach((data, idx) => {
              const row: pptxgen.TableRow = [{ text: `${idx + 1}` }];

              if (data.company_id.id === "PEER_SET_AVG") {
                row.push({
                  text: `${data?.company_id?.label?.toString() ?? "Unknown Company"} (${peer_set_type.label})`,
                });
              } else {
                row.push({ text: data?.company_id?.label?.toString() ?? "Unknown Company" });
              }

              measure_names.forEach((measure_name) => {
                const measure = data?.[measure_name];
                let text = measure.score?.toString();
                if (measure.signal === "NO_SIGNAL") {
                  text = "No Signal";
                }
                if (measure.signal === "LOW_SIGNAL") {
                  text = `Low (${measure.score})`;
                }
                if (measure === undefined) {
                  text = "Unknown Topic";
                }
                row.push({ text });
              });

              table.push(row);
            });

            const output: IRC_Exportable_PPTX_Table = {
              title: `Analysis: Table - ${current_measure?.label}`,
              data: table,
              options: {
                x: 0.5,
                y: 0.9,
                w: 9.0,
                h: 5.0,
                color: "363636",
                fill: { color: "F2F2F2" },
                border: { type: "solid" },
                autoPage: true,
                autoPageRepeatHeader: true,
                autoPageHeaderRows: 1,
                fontSize: 8,
                colW: 1.5,
                rowH: 0.3,
                margin: 0.1,
                valign: "middle",
                align: "left",
                autoPageLineWeight: 0.1,
              },
            };

            return output;
          },

          eps_table: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
            const measures_data = await legend.utils.awaitObservable(
              instance.remote.measures.data,
              instance.remote.measures.pending,
              instance.remote.measures.error,
            );

            const peerset = report_builder.remote.peerset.data.get();
            const eps_data = measures_data.eps_data || [];

            if (!eps_data.length) {
              throw new Error("No EPS data available for table generation");
            }

            // Transform EPS data for table visualization
            const tableData = eps_data
              .map((measure, index) => {
                const company = peerset[measure.company_id];
                const score =
                  typeof measure.measure_value === "string"
                    ? parseFloat(measure.measure_value.replace(/[+\-%]/g, ""))
                    : measure.measure_value;

                return {
                  company_name: company?.company_name || "Unknown Company",
                  eps_score: score || 0,
                  display_value: typeof measure.measure_value === "string" ? measure.measure_value : `${score || 0}%`,
                  rank: index + 1,
                };
              })
              .sort((a, b) => b.eps_score - a.eps_score) // Sort by EPS score descending
              .map((item, index) => ({ ...item, rank: index + 1 })); // Update rank after sorting

            const table: pptxgen.TableRow[] = [];

            // Create header row
            const headerRow: pptxgen.TableRow = [{ text: "Rank" }, { text: "Company" }, { text: "Net Rating" }];
            table.push(headerRow);

            // Create data rows
            tableData.forEach((data) => {
              const row: pptxgen.TableRow = [
                { text: data.rank.toString() },
                { text: data.company_name },
                { text: data.display_value },
              ];
              table.push(row);
            });

            const output: IRC_Exportable_PPTX_Table = {
              title: "Net Rating Ranking",
              data: table,
              options: {
                x: 0.5,
                y: 0.9,
                w: 8.0,
                h: 5.0,
                color: "363636",
                fill: { color: "F2F2F2" },
                border: { type: "solid" },
                autoPage: true,
                autoPageRepeatHeader: true,
                autoPageHeaderRows: 1,
                fontSize: 10,
                colW: [1.0, 4.0, 3.0], // Custom column widths: Rank, Company, Score
                rowH: 0.4,
                margin: 0.1,
                valign: "middle",
                align: "left",
                autoPageLineWeight: 0.1,
              },
            };

            return output;
          },
        },
        csv: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
          // measures_data is a remote observable, so we need to await it whilst it is fetched from the server
          const measures_data = await legend.utils.awaitObservable(
            instance.remote.measures.data,
            instance.remote.measures.pending,
            instance.remote.measures.error,
          );

          const chart_config = instance.local.views.analysis.chart_config.get();
          const peerset = report_builder.remote.peerset.data.get();

          const current_measure_id = instance.local.views.analysis.selectors.measure.selectedMeasureId.get();
          const current_measure = instance.local.config.measure.measures
            .get()
            .find((measure) => measure.id === current_measure_id);

          const axisKey = chart_config.polar_axis.data_key;
          if (!axisKey) {
            throw new Error("X-axis data key not found");
          }

          // Convert company IDs to names in headers, handling benchmarks
          const originalHeaders = Object.keys(measures_data.chart_data[0]).filter((key) => key !== axisKey);
          const convertedHeaders = originalHeaders.map((header: string) => {
            const isBenchmark = header.startsWith("benchmark_");
            if (isBenchmark) {
              return sanitizeForCSV(header.replace("benchmark_", "Benchmark: "));
            }
            return sanitizeForCSV(mapMeasureNameToLabel(header) ?? header);
          });

          // Create CSV header with measure and topic columns
          const headers = ["Measure", "Rank", capitalize("Company"), ...convertedHeaders];
          const csvContent = [headers.join(",")];

          // Create CSV rows with measure and topic values
          measures_data.chart_data.forEach((item, idx) => {
            const measureLabel = current_measure?.label || "";
            const company_id = item[axisKey]?.toString();
            const rowData = [
              sanitizeForCSV(measureLabel),
              sanitizeForCSV((idx + 1).toString()),
              sanitizeForCSV(company_id ? peerset[company_id].company_name : ""),
              ...originalHeaders.map((header) => sanitizeForCSV(item[header]?.toString() || "")),
            ];
            csvContent.push(rowData.join(","));
          });

          const output = csvContent.join("\n");
          return output;
        },

        eps_csv: async (instance: ReturnType<typeof rc.registry.actions.getInstance>) => {
          const measures_data = await legend.utils.awaitObservable(
            instance.remote.measures.data,
            instance.remote.measures.pending,
            instance.remote.measures.error,
          );

          const peerset = report_builder.remote.peerset.data.get();
          const eps_data = measures_data.eps_data || [];

          if (!eps_data.length) {
            throw new Error("No EPS data available for CSV generation");
          }

          // Transform EPS data for CSV export
          const csvData = eps_data
            .filter((measure) => measure.measure_signal !== "NO_SIGNAL")
            .map((measure, index) => {
              const company = peerset[measure.company_id];
              const score =
                typeof measure.measure_value === "string"
                  ? parseFloat(measure.measure_value.replace(/[+\-%]/g, ""))
                  : measure.measure_value;

              return {
                company_id: measure.company_id,
                company_name: company?.company_name || "Unknown Company",
                eps_score: score || 0,
                display_value: typeof measure.measure_value === "string" ? measure.measure_value : `${score || 0}%`,
                signal: measure.measure_signal,
                rank: index + 1,
              };
            })
            .sort((a, b) => b.eps_score - a.eps_score) // Sort by EPS score descending
            .map((item, index) => ({ ...item, rank: index + 1 })); // Update rank after sorting

          // Create CSV header
          const headers = ["Rank", "Company ID", "Company Name", "Net Rating (%)"];
          const csvContent = [headers.map((h) => sanitizeForCSV(h)).join(",")];

          // Create CSV rows
          csvData.forEach((data) => {
            const rowData = [
              sanitizeForCSV(data.rank.toString()),
              sanitizeForCSV(data.company_id),
              sanitizeForCSV(data.company_name),
              sanitizeForCSV(data.eps_score.toString()),
              // sanitizeForCSV(data.signal),
            ];
            csvContent.push(rowData.join(","));
          });

          const output = csvContent.join("\n");
          return output;
        },
      },
    },
  },
};
